import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, ChevronRight } from 'lucide-react'
import type { ChecklistItem } from '@/types/api'
import {
  CHECKLIST_STEP_KEYS,
  getOnboardingCurrentStepIndex,
  mergeChecklistItems,
  persyaratanCddsHref,
  type CddsNavContext,
  type ChecklistStepKey,
} from '@/lib/onboarding-next-step'
import { needsCdds10Submit } from '@/lib/cdds-rules'

export { CHECKLIST_STEP_KEYS, type ChecklistStepKey } from '@/lib/onboarding-next-step'

interface OnboardingStepperProps {
  items: ChecklistItem[]
  clientId: string | null
  isLoading?: boolean
  onOpenLive: () => void
  awaitingApproval?: boolean
  cddsNav?: CddsNavContext
}

function getStepLabelKey(key: ChecklistStepKey): string {
  return `checklist.${key}`
}

export type StepAction =
  | { type: 'link'; href: string }
  | { type: 'button'; onClick: () => void }
  | { type: 'text' }
  | { type: 'disabled' }

export function getStepAction(
  key: ChecklistStepKey,
  completed: boolean,
  clientId: string | null,
  onOpenLive: () => void,
  cddsNav?: CddsNavContext,
): StepAction {
  const pendingCdds10 =
    key === 'persyaratan_accepted' && needsCdds10Submit(cddsNav ?? {})
  if (completed && !pendingCdds10) return { type: 'text' }

  switch (key) {
    case 'email_verified':
      return { type: 'link', href: '/verify-email' }
    case 'live_account_created':
      return { type: 'button', onClick: onOpenLive }
    case 'form_perjanjian_filled':
      return { type: 'link', href: '/register/data' }
    case 'persyaratan_accepted':
      return { type: 'link', href: persyaratanCddsHref(cddsNav ?? {}) }
    case 'perjanjian_accepted':
      return { type: 'text' }
    case 'deposit_funds':
      return { type: 'link', href: '/funding' }
    default:
      return { type: 'text' }
  }
}

/** One row: the whole row is the tap target when the step has an action. */
function StepRow({ action, children }: { action: StepAction; children: ReactNode }) {
  if (action.type === 'link') {
    return (
      <Link to={action.href} className="prod-steps__row prod-steps__row--action">
        {children}
        <ChevronRight className="prod-steps__chevron" aria-hidden="true" />
      </Link>
    )
  }
  if (action.type === 'button') {
    return (
      <button type="button" className="prod-steps__row prod-steps__row--action" onClick={action.onClick}>
        {children}
        <ChevronRight className="prod-steps__chevron" aria-hidden="true" />
      </button>
    )
  }
  return <div className="prod-steps__row">{children}</div>
}

/**
 * Progress rail. The line is filled up to the current step (how far along the path the client is);
 * the markers say what is done, including steps finished out of order.
 */
export function OnboardingStepper({
  items,
  clientId,
  isLoading,
  onOpenLive,
  awaitingApproval = false,
  cddsNav,
}: OnboardingStepperProps) {
  const { t } = useTranslation()
  const steps = mergeChecklistItems(items)
  const allComplete = steps.every((item) => item.completed)

  if (!isLoading && allComplete) return null

  const currentIndex = getOnboardingCurrentStepIndex(steps, {
    awaitingApproval,
    cddsNav,
    isLoading,
  })

  return (
    <ol className={`prod-steps${isLoading ? ' prod-steps--loading' : ''}`}>
      {steps.map((item, index) => {
        const key = item.key as ChecklistStepKey
        const isDone = item.completed
        const isCurrent = !isLoading && index === currentIndex
        const state = isCurrent ? 'current' : isDone ? 'done' : 'upcoming'
        const passed = !isLoading && currentIndex > index
        const label = t(getStepLabelKey(key), { defaultValue: item.key })
        const action: StepAction = isLoading
          ? { type: 'text' }
          : getStepAction(key, isDone, clientId, onOpenLive, cddsNav)
        const status = isCurrent
          ? key === 'perjanjian_accepted'
            ? t('home.steps.inReview')
            : t('home.steps.current')
          : isDone
            ? t('home.steps.done')
            : null

        return (
          <li
            key={item.key}
            className={`prod-steps__item prod-steps__item--${state}${passed ? ' prod-steps__item--passed' : ''}`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <span className="prod-steps__marker" aria-hidden="true">
              {isDone && !isCurrent ? (
                <Check className="prod-steps__check" />
              ) : isCurrent ? (
                <span className="prod-steps__pip" />
              ) : (
                index + 1
              )}
            </span>
            <StepRow action={action}>
              <span className="prod-steps__text">
                <span className="prod-steps__label">{label}</span>
                {status ? <span className="prod-steps__status">{status}</span> : null}
              </span>
            </StepRow>
          </li>
        )
      })}
    </ol>
  )
}
